import React from 'react';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {TagSchemasSSIQuery} from 'bulk-import/panes/__generated/TagSchemasSSIQuery';
import {TAG_SCHEMAS_SSI_QUERY} from 'bulk-import/panes/EntitySchemaImplementingSSISelectionWithRestrictions';
import {getRegistrySupportedOligoAwareTypes, TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {TRIAL_SAMPLE_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';

export interface RegistrationTableTagSchemaSelectorProps {
  onChange: (schema: TagSchema) => void;
  selectedTagSchemaId: string | null;
  ssisToExclude?: Array<string>;
}

/**
 * A component that wraps TagSchemaSelector with SSI filtering logic for registration tables.
 * Filters out schemas implementing TrialSampleSSI since trial samples can only be created via Registry ->
 * Import trial samples.
 * Also optionally filters out schemas implementing the given SSIs.
 */
export function RegistrationTableTagSchemaSelector({
  onChange,
  selectedTagSchemaId,
  ssisToExclude,
}: RegistrationTableTagSchemaSelectorProps): JSX.Element {
  return (
    // Fetch interfaces so we can filter.
    <BenchlingQuery<TagSchemasSSIQuery>
      query={TAG_SCHEMAS_SSI_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {(data) => {
        const interfaceIdsBySchemaId = Object.fromEntries(
          data.tagSchemas.map((schema) => [
            schema.id,
            schema.interfaceConfigs.map((interfaceConfig) => interfaceConfig.interface.id),
          ])
        );

        const tagSchemaFilterFn = (schema: TagSchema): boolean => {
          // This will be eventually replaced with a more general SSI filter where all SSIs are opt-out by
          // default. See https://benchling.atlassian.net/wiki/x/xgSUT
          const isTrialSampleSchema = (interfaceIdsBySchemaId[schema.id] ?? []).includes(
            TRIAL_SAMPLE_SCHEMA_INTERFACE_ID
          );
          if (isTrialSampleSchema) {
            return false;
          }
          const isAntibodySchema = (interfaceIdsBySchemaId[schema.id] ?? []).includes(
            ANTIBODY_SCHEMA_INTERFACE_ID
          );
          if (
            ssisToExclude?.some((ssiToExclude) => interfaceIdsBySchemaId[schema.id]?.includes(ssiToExclude))
          ) {
            return false;
          }
          return !isAntibodySchema || BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_REG_TABLES;
        };

        return (
          <TagSchemaSelector
            onChange={onChange}
            selectedTagSchemaId={selectedTagSchemaId}
            tagSchemaFilterFn={tagSchemaFilterFn}
            requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
            oligoAwareFolderItemTypes={getRegistrySupportedOligoAwareTypes()}
          />
        );
      }}
    </BenchlingQuery>
  );
}
